import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { MediumButton } from '../../styles/Buttons'
import AddInputBtn from '../AddInputBtn'
import DeleteInputBtn from '../DeleteInputBtn'
import { Color, Colors } from '../../styles/Colors'
import { ExpandTextArea, Form } from '../../styles/FormStyle'
import { useTranslation } from 'react-i18next'
import FormActionBtns from '../FormActionBtns';

const CustomSelect = styled.div`
  position: relative; 
  display: block; 
  height: 36px; 
  margin: 0 0 1rem 0; 

  & > select {
    appearance: none; 
    width: 100%; 
    border: 1px solid #fff; 
    border-radius: 4px; 
    cursor: pointer; 
    background-color: ${({$theme}) => (($theme === 'lightBlue') ? Color(Colors.blue) : Color(Colors.lightBlue))};
    color: ${Color(Colors.white)};
    border: none;  
  }

  & > select::-ms-expand {
    display: none; 
  }

  & > select:focus {
    outline: 2px solid ${Color(Colors.white)}; 
  }

  &:after {
    content: "▼";
    position: absolute;
    top: 50%;
    right: 16px; 
    transform: translateY(-50%)
        rotate(${({$isCustomOption}) => ($isCustomOption ? '180deg' : '0deg')});
    transition: transform 0.5s ease;  
    pointer-events: none; 
    color: ${Color(Colors.white)};
  }
`;

const InputWrapper = styled.div`
    display: grid; 
    grid-template-columns: 2fr 3.2rem; 
    position: relative; 

    /* Input fields */ 
    & > div:first-of-type {
        display: flex;
        justify-content: center; 
        align-items: flex-end; 
        gap: 1rem;
        margin: 0 0 1rem 0;
            
        & > div > input {
            margin: 1rem 0 0 0; 
        } 
            
        & > div:first-of-type,
        & > div:nth-of-type(2){
            flex: 1; 
        }
    }

    /* Delete button */ 
    & > div:last-of-type {
        position: absolute; 
        right: 0; 
        top: 35px; 
    }

`;

const ActionForm = ({ item, setItem, handleOnChange, handleSubmit, handleEditCancel, title, type, dynamicFields, buttonColorTheme }) => {
    const { t } = useTranslation(); 
    const [height, setHeight] = useState(100);
    const [isCustomOption, setIsCustomOption] = useState(false);
    const textareaRef = useRef(null);

    const handleMouseDown = (event) => {
        const startY = event.clientY;
        const startHeight = textareaRef.current.offsetHeight;

        const onMouseMove = (e) => {
            const newHeight = startHeight + (e.clientY - startY);
            // Set a minimum height if desired
            if (newHeight > 50) {
                setHeight(newHeight);
            }
        };

        const onMouseUp = () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    };

    const actionOptions = [
        { value: t('actionDocs.tableHeadTitles.action.optionList.customAction'), name: t('actionDocs.tableHeadTitles.action.optionList.customAction') },
        { value: t('actionDocs.tableHeadTitles.action.optionList.yesWithComment'), name:  t('actionDocs.tableHeadTitles.action.optionList.yesWithComment') },
        { value: t('actionDocs.tableHeadTitles.action.optionList.yes'), name: t('actionDocs.tableHeadTitles.action.optionList.yes') },
        { value: t('actionDocs.tableHeadTitles.action.optionList.abstain'), name: t('actionDocs.tableHeadTitles.action.optionList.abstain') },
        { value: t('actionDocs.tableHeadTitles.action.optionList.inFavor'), name: t('actionDocs.tableHeadTitles.action.optionList.inFavor') },
        { value: t('actionDocs.tableHeadTitles.action.optionList.notApplicable'), name: t('actionDocs.tableHeadTitles.action.optionList.notApplicable') },
        { value: t('actionDocs.tableHeadTitles.action.optionList.noComments'), name: t('actionDocs.tableHeadTitles.action.optionList.noComments') },
        { value: t('actionDocs.tableHeadTitles.action.optionList.discussAndVote'), name: t('actionDocs.tableHeadTitles.action.optionList.discussAndVote') },
        { value: t('actionDocs.tableHeadTitles.action.optionList.checkAvailability'), name: t('actionDocs.tableHeadTitles.action.optionList.checkAvailability')},
        { value: t('actionDocs.tableHeadTitles.action.optionList.discussAndComment'), name: t('actionDocs.tableHeadTitles.action.optionList.discussAndComment') },
        { value: t('actionDocs.tableHeadTitles.action.optionList.discussConsultation'), name: t('actionDocs.tableHeadTitles.action.optionList.discussConsultation') },
        { value: t('actionDocs.tableHeadTitles.action.optionList.reviewAndApprove'), name: t('actionDocs.tableHeadTitles.action.optionList.reviewAndApprove') },
        { value: t('actionDocs.tableHeadTitles.action.optionList.checkPlenaryAttendance'), name: t('actionDocs.tableHeadTitles.action.optionList.checkPlenaryAttendance') },
        { value: t('actionDocs.tableHeadTitles.action.optionList.reviewAndRespond'), name: t('actionDocs.tableHeadTitles.action.optionList.reviewAndRespond') },
    ]

    const renderActionOptions = () => {
        const isVote = item?.vote && Object.prototype.hasOwnProperty.call(item.vote, 'answer');

        const fieldName = isVote ? 'vote.answer' : 'action'; 
        const customFieldName = isVote ? 'vote.custom_answer' : 'custom_action'; 
        const selectedValue = isVote ? item.vote?.answer : item.action; 
        const customSelectedValue = isVote ? item.vote?.custom_answer : item.custom_action; 

        const filterDuplicatedOptions = actionOptions.filter(option => option.value !== selectedValue);
        const optionsToRender =
            selectedValue
                ? [{ value: selectedValue, name: selectedValue }, ...filterDuplicatedOptions]
                : filterDuplicatedOptions

        return (
            <>
                <label>{t('actionDocs.tableHeadTitles.action.name')}</label>
                <CustomSelect
                    $theme={buttonColorTheme}
                    $isCustomOption={isCustomOption}
                >
                    <select
                        name={fieldName}
                        value={selectedValue || ''}
                        onChange={handleOnChange}
                        onFocus={() => setIsCustomOption(true)}
                        onBlur={() => setIsCustomOption(false)}
                    >
                        <option value='' disabled>{t('actionDocs.tableHeadTitles.action.optionList.select')}</option>
                        {optionsToRender.map((option) => (
                            <option key={option.value} value={option.value}>{option.name}</option>
                        ))}
                    </select>
                </CustomSelect>
                {selectedValue === t('actionDocs.tableHeadTitles.action.optionList.customAction') && (
                    <input
                        type='text'
                        placeholder={`${t('actionDocs.tableHeadTitles.action.optionList.customAction')} ${t('common.here')}...`}
                        name={customFieldName}
                        value={customSelectedValue}
                        onChange={handleOnChange}
                        spellCheck={false}
                    />
                )}
            </>
        );
    };

    return (
        <Form $theme={buttonColorTheme} onSubmit={handleSubmit}>
            <h5>{title}</h5>
            <div>
                {dynamicFields.map((field, index) => (
                    <div key={index}>
                        <label>
                            {field.label}
                            {!!field.required === true && (
                                <span> *</span>
                            )}
                        </label>
                        {field.type === 'textarea' ? (
                            <ExpandTextArea onMouseDown={handleMouseDown}>
                                <textarea
                                    key={index}
                                    name={field.name}
                                    placeholder={field.placeholder}
                                    value={item[field.name]}
                                    onChange={handleOnChange}
                                    required={field.required === true}
                                    ref={textareaRef}
                                    style={{ height: `${height}px`, resize: 'none', padding: '8px' }}
                                    spellCheck={false}
                                ></textarea>
                                <div></div>
                            </ExpandTextArea>
                        ) : (
                            <input
                                key={index}
                                type={field.type}
                                name={field.name}
                                placeholder={field.placeholder}
                                value={item[field.name]}
                                onChange={handleOnChange}
                                required={field.required === true}
                            />
                        )}
                    </div>
                ))}
                <div>
                    <label>{`${t('actionDocs.tableHeadTitles.deadline.name')}*`}</label>
                    <input
                        type='date'
                        name='deadline'
                        placeholder={t('actionDocs.tableHeadTitles.deadline.label')}
                        min='0000-01-01'
                        max='9999-12-31'
                        required
                        value={item.deadline.split('T')?.[0]}
                        onChange={handleOnChange}
                    />
                </div>
                {item.document_links && item.document_links.map((link, index) => (
                    <InputWrapper key={index}>
                        <div>
                            <div>
                                <label>{`${t('common.formLabels.fileName.name')} #${index + 1}`}</label>
                                <input
                                    type='text'
                                    name={`document_links.${index}.fileName`}
                                    placeholder={`${t('common.formLabels.fileName.label')}`}
                                    value={link.fileName}
                                    onChange={handleOnChange}
                                    spellCheck={false}
                                />
                            </div>
                            <div>
                                 <label>{`${t('common.formLabels.url.name')} #${index + 1}`}</label>
                                <input
                                    type='text'
                                    name={`document_links.${index}.url`}
                                    placeholder={`${t('common.formLabels.url.label')}`}
                                    value={link.url}
                                    onChange={handleOnChange}
                                    spellCheck={false}
                                />
                            </div>
                        </div>
                        <div>
                            <DeleteInputBtn
                                item={item}
                                setItem={setItem}
                                index={index}
                                theme={buttonColorTheme}
                            />
                        </div>
                    </InputWrapper>
                ))}
                {(item.document_links.length === 1 || item.document_links.length === 0) && (
                    <AddInputBtn
                        item={item}
                        setItem={setItem}
                        label={`${t('common.addBtnLabel')} ${t('common.formLabels.fileName.btnLabel')}`}
                        theme={buttonColorTheme}
                    />
                )}
                <div>
                </div>
                {renderActionOptions()}
            </div>
            {type === 'edit_task' ? (
                <FormActionBtns 
                    theme={buttonColorTheme}
                    handleEditCancel={handleEditCancel}
                />
            ) : (
                <MediumButton
                    type='submit'
                    $theme={buttonColorTheme}
                >
                    {t('common.addBtnLabel')}
                </MediumButton>
                   
            )}
        </Form>
    )
}

export default ActionForm